/**
 * Autenticación de PIN CONECT.
 *
 * Hay un solo lugar donde vive el permiso: la colección `acceso`, con el
 * CORREO como id del documento.
 *
 *   acceso/dilan@gmail.com     -> { rol:'admin', proveedor:'google.com', active }
 *   acceso/marta@gmail.com     -> { rol:'inversionista', proveedor:'google.com',
 *                                   comunidadId, codigo, active }
 *   acceso/1010@pinconect.app  -> { rol:'vendedor', proveedor:'password',
 *                                   comunidadId, codigo:'1010', codigoNum:1010, active }
 *
 * El administrador y los inversionistas entran con Google. Los vendedores
 * siguen escribiendo su código de siempre: por debajo es una cuenta de
 * Firebase Authentication con un correo interno `<codigo>@pinconect.app`, que
 * nadie de afuera puede poseer.
 *
 * Lo que cambia frente a antes: el código deja de estar guardado en un
 * documento legible, y Firebase empieza a bloquear los intentos por fuerza
 * bruta. Antes no había ningún límite y la clave del administrador se podía
 * leer con la API key del bundle.
 */

import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signOut,
  deleteUser,
  linkWithCredential,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth'
import { initializeApp, getApp } from 'firebase/app'
import { getFirestore, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'

/** Dominio interno de los vendedores. No es un dominio real a propósito. */
export const DOMINIO_INTERNO = 'pinconect.app'

/**
 * Sufijo que se le añade al código para cumplir el mínimo de 6 caracteres que
 * exige Firebase. No es un secreto —va en el bundle— y no pretende serlo: lo
 * que protege es que el código ya no se puede leer de Firestore y que
 * Authentication limita los intentos.
 */
const SUFIJO = '::pinconect'

export function correoDeCodigo (codigo) {
  return `${String(codigo).trim()}@${DOMINIO_INTERNO}`
}

function claveDeCodigo (codigo) {
  return `${String(codigo).trim()}${SUFIJO}`
}

/* ------------------------------------------------------------------ */
/* Lectura del permiso                                                 */
/* ------------------------------------------------------------------ */

/** Lee acceso/{correo}. Devuelve null si esa persona no está autorizada. */
export async function leerAcceso (correo) {
  const db = getFirestore()
  const snap = await getDoc(doc(db, 'acceso', String(correo).toLowerCase()))
  return snap.exists() ? { ...snap.data(), correo: snap.id } : null
}

/**
 * Comprueba que quien entró tenga permiso y el rol esperado.
 * Si no lo tiene, cierra la sesión: no se deja a nadie a medio entrar.
 */
async function validar (user, rolesPermitidos) {
  if (!user || !user.email) {
    await salir()
    return { ok: false, motivo: 'No se pudo leer el correo de la cuenta' }
  }

  const permiso = await leerAcceso(user.email)

  if (!permiso) {
    await salir()
    return {
      ok: false,
      // El correo va completo a proposito: es el dato exacto con el que hay
      // que crear el permiso, y de Google puede llegar distinto al esperado.
      motivo: `Entraste con ${user.email}, y ese correo no tiene acceso. `
        + 'Pídele al administrador que te autorice con ese correo, tal como aparece aquí.',
      correo: user.email
    }
  }

  if (permiso.active === false) {
    await salir()
    return { ok: false, motivo: 'Tu acceso está desactivado. Contacta al administrador.' }
  }

  if (rolesPermitidos.length && !rolesPermitidos.includes(permiso.rol)) {
    await salir()
    const esperado = rolesPermitidos.join(' o ')
    return {
      ok: false,
      motivo: `${user.email} está registrado como "${permiso.rol || 'sin rol'}", `
        + `y esta aplicación es para "${esperado}". Puede que hayas abierto la aplicación equivocada.`,
      correo: user.email
    }
  }

  return { ok: true, permiso, user }
}

/* ------------------------------------------------------------------ */
/* Entrar                                                              */
/* ------------------------------------------------------------------ */

/** Entrada con Google: administrador e inversionistas. */
export async function entrarConGoogle (rolesPermitidos = []) {
  const auth = getAuth()
  try {
    await setPersistence(auth, browserLocalPersistence)
    const proveedor = new GoogleAuthProvider()
    proveedor.setCustomParameters({ prompt: 'select_account' })
    const res = await signInWithPopup(auth, proveedor)
    return await validar(res.user, rolesPermitidos)
  } catch (error) {
    return { ok: false, motivo: mensajeDeError(error) }
  }
}

/** Entrada con código: vendedores. */
export async function entrarConCodigo (codigo, rolesPermitidos = ['vendedor']) {
  const auth = getAuth()
  const limpio = String(codigo || '').trim()
  if (!limpio) return { ok: false, motivo: 'Escribe tu código' }

  try {
    await setPersistence(auth, browserLocalPersistence)
    const res = await signInWithEmailAndPassword(
      auth, correoDeCodigo(limpio), claveDeCodigo(limpio)
    )
    return await validar(res.user, rolesPermitidos)
  } catch (error) {
    return { ok: false, motivo: mensajeDeError(error) }
  }
}

/* ------------------------------------------------------------------ */
/* Sesión abierta                                                      */
/* ------------------------------------------------------------------ */

/**
 * Resuelve con el permiso de la sesión que ya estaba abierta, o con null.
 * Sirve para no volver a pedir la entrada en cada recarga.
 */
export function sesionActual (rolesPermitidos = []) {
  return new Promise(resolve => {
    const auth = getAuth()
    const dejarDeEscuchar = onAuthStateChanged(auth, async user => {
      dejarDeEscuchar()
      if (!user) return resolve(null)
      const r = await validar(user, rolesPermitidos)
      resolve(r.ok ? r : null)
    })
  })
}

export async function salir () {
  try {
    await signOut(getAuth())
  } catch (error) {
    // si no había sesión, no hay nada que cerrar
  }
}

/* ------------------------------------------------------------------ */
/* Alta de cuentas (solo el panel de administración)                   */
/* ------------------------------------------------------------------ */

/**
 * Crea la cuenta de Authentication de un vendedor.
 *
 * Se usa una instancia secundaria de Firebase porque
 * createUserWithEmailAndPassword deja la sesión abierta con el usuario recién
 * creado: en la instancia principal eso sacaría al administrador de su propia
 * sesión a mitad del trabajo.
 */
function appSecundaria () {
  const principal = getApp()
  try {
    return getApp('altaDeCuentas')
  } catch (error) {
    return initializeApp(principal.options, 'altaDeCuentas')
  }
}

export async function crearCuentaDeCodigo (codigo) {
  const auth = getAuth(appSecundaria())
  try {
    const res = await createUserWithEmailAndPassword(
      auth, correoDeCodigo(codigo), claveDeCodigo(codigo)
    )
    await signOut(auth)
    return { ok: true, uid: res.user.uid }
  } catch (error) {
    await signOut(auth).catch(() => {})
    if (error.code === 'auth/email-already-in-use') {
      // La cuenta ya existía de una migración anterior: no es un error.
      return { ok: true, yaExistia: true }
    }
    return { ok: false, motivo: mensajeDeError(error) }
  }
}

/** Borra la cuenta de Authentication de un vendedor, si se puede. */
export async function borrarCuentaDeCodigo (codigo) {
  const auth = getAuth(appSecundaria())
  try {
    const res = await signInWithEmailAndPassword(
      auth, correoDeCodigo(codigo), claveDeCodigo(codigo)
    )
    await deleteUser(res.user)
    return { ok: true }
  } catch (error) {
    await signOut(auth).catch(() => {})
    // Si la cuenta ya no existe, el resultado es el que se quería.
    return { ok: error.code === 'auth/user-not-found' }
  }
}

/* ------------------------------------------------------------------ */
/* Permisos                                                            */
/* ------------------------------------------------------------------ */

export async function darAcceso ({ correo, rol, proveedor, nombre, comunidadId, codigo, confirmarCambioDeRol = false }) {
  const db = getFirestore()
  const id = String(correo || '').trim().toLowerCase()

  if (!id || !id.includes('@')) throw new Error('Escribe un correo válido')
  if (!['admin', 'vendedor', 'inversionista'].includes(rol)) throw new Error('Rol inválido')

  // Este correo puede ya tener otro rol (por ejemplo, el mismo correo que
  // usa el administrador para entrar al panel). Sobrescribirlo en silencio
  // le quita el acceso que ya tenía sin que nadie lo note hasta que falla
  // el login. Por eso hay que confirmarlo explícitamente.
  if (!confirmarCambioDeRol) {
    const actual = await leerAcceso(id)
    if (actual && actual.rol && actual.rol !== rol) {
      const error = new Error(
        `${id} ya tiene acceso como "${actual.rol}". Si continúas, pierde ese acceso y queda como "${rol}".`
      )
      error.code = 'rol-en-conflicto'
      error.rolActual = actual.rol
      throw error
    }
  }

  const datos = {
    rol,
    proveedor: proveedor || 'google.com',
    nombre: String(nombre || '').trim(),
    active: true,
    creadoEn: serverTimestamp()
  }

  if (comunidadId) datos.comunidadId = comunidadId
  if (codigo !== undefined && codigo !== null && codigo !== '') {
    datos.codigo = String(codigo)
    const n = parseInt(String(codigo), 10)
    if (!Number.isNaN(n)) datos.codigoNum = n
  }

  await setDoc(doc(db, 'acceso', id), datos, { merge: true })
  return id
}

export async function quitarAcceso (correo) {
  const db = getFirestore()
  await deleteDoc(doc(db, 'acceso', String(correo).trim().toLowerCase()))
}

export async function cambiarEstadoAcceso (correo, active) {
  const db = getFirestore()
  await setDoc(doc(db, 'acceso', String(correo).trim().toLowerCase()), { active }, { merge: true })
}

/* ------------------------------------------------------------------ */

function mensajeDeError (error) {
  const codigo = String(error && error.code)
  const mapa = {
    'auth/invalid-credential': 'Código incorrecto',
    'auth/wrong-password': 'Código incorrecto',
    'auth/user-not-found': 'Código incorrecto',
    'auth/invalid-email': 'Código incorrecto',
    'auth/user-disabled': 'Esta cuenta está bloqueada',
    'auth/too-many-requests': 'Demasiados intentos fallidos. Espera unos minutos.',
    'auth/popup-closed-by-user': 'Cerraste la ventana de Google antes de terminar',
    'auth/cancelled-popup-request': 'Quedó otra ventana de Google abierta',
    'auth/popup-blocked': 'El navegador bloqueó la ventana de Google. Permítela e intenta de nuevo.',
    'auth/unauthorized-domain': 'Este dominio no está autorizado en Firebase Authentication.',
    'auth/operation-not-allowed': 'El inicio de sesión con Google no está habilitado en Firebase.'
  }
  return mapa[codigo] || `No se pudo entrar (${codigo})`
}

/* ------------------------------------------------------------------ */
/* PIN adicional del administrador                                     */
/* ------------------------------------------------------------------ */

/**
 * El panel exige un PIN además de Google: abrir la app con la sesión de
 * Google recordada no debe bastar para entrar.
 *
 * El PIN se implementa como una SEGUNDA credencial (contraseña) enlazada a
 * la MISMA cuenta de Firebase Auth que ya abrió con Google. Verificarlo pasa
 * por los servidores de Firebase (reauthenticateWithCredential), así que
 * hereda el bloqueo por intentos fallidos de Authentication.
 *
 * Es deliberado no guardar el PIN (ni su hash) en Firestore para compararlo
 * en el navegador: quien ya tuviera esa sesión de Google abierta podría
 * probar las 10.000 combinaciones de un PIN de 6 dígitos en el cliente, sin
 * ningún límite. Enlazarlo como credencial real de Authentication evita
 * exactamente eso.
 */

const LARGO_PIN_ADMIN = 6

export function pinAdminValido (pin) {
  return new RegExp(`^\\d{${LARGO_PIN_ADMIN}}$`).test(String(pin || '').trim())
}

/** ¿Esta cuenta ya tiene un PIN configurado? */
export function tienePinConfigurado (user) {
  return user.providerData.some(p => p.providerId === 'password')
}

/** Mensajes especificos del PIN, por encima de los genericos de Google/codigo. */
function mensajeDePin (error) {
  const codigo = String(error && error.code)
  const mapa = {
    'auth/invalid-credential': 'PIN incorrecto',
    'auth/wrong-password': 'PIN incorrecto',
    'auth/too-many-requests': 'Demasiados intentos fallidos. Espera unos minutos.',
    'auth/credential-already-in-use': 'Ese PIN ya esta en uso por otra cuenta. Elige otro.',
    'auth/provider-already-linked': 'Esta cuenta ya tiene un PIN configurado.',
    'auth/requires-recent-login': 'Tu sesion de Google expiro. Vuelve a entrar con Google.'
  }
  return mapa[codigo] || mensajeDeError(error)
}

/** Enlaza el PIN elegido como contraseña de esta cuenta. Solo la primera vez. */
export async function establecerPinAdmin (user, pin) {
  if (!pinAdminValido(pin)) {
    return { ok: false, motivo: `El PIN debe tener ${LARGO_PIN_ADMIN} dígitos` }
  }
  try {
    const credencial = EmailAuthProvider.credential(user.email, pin)
    await linkWithCredential(user, credencial)
    return { ok: true }
  } catch (error) {
    return { ok: false, motivo: mensajeDePin(error) }
  }
}

/** Verifica el PIN contra Firebase Auth. Se bloquea solo tras varios fallos. */
export async function verificarPinAdmin (user, pin) {
  try {
    const credencial = EmailAuthProvider.credential(user.email, pin)
    await reauthenticateWithCredential(user, credencial)
    return { ok: true }
  } catch (error) {
    return { ok: false, motivo: mensajeDePin(error) }
  }
}
